import {useAuth} from '@/shared/contexts/AuthContext.tsx';
import {UserRole} from '@/shared/types/common.ts';
import {ShieldX} from 'lucide-react';
import {useCallback, useState} from 'react';

const ROLE_LABELS: Record<UserRole, string> = {
    owner: 'Proprietário',
    receptionist: 'Recepção',
    employee: 'Profissional',
};

const getRoleLabel = (role: string): string => {
    return ROLE_LABELS[role as UserRole] ?? role;
};

export const LoginPage = () => {
    const {signInWithGoogle, loading, isAuthenticated, hasAccess, employee, signOut} = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [signingIn, setSigningIn] = useState(false);

    const handleGoogleSignIn = useCallback(async () => {
        setError(null);
        setSigningIn(true);
        try {
            await signInWithGoogle();
        } catch {
            setError('Erro ao fazer login. Tente novamente.');
            setSigningIn(false);
        }
    }, [signInWithGoogle]);

    // Show loading state
    if (loading) {
        return (
            <div className="login-page">
                <div className="login-card">
                    <LoginLogo />
                    <div className="login-loading">
                        <div className="spinner" />
                        <span>Carregando...</span>
                    </div>
                </div>
            </div>
        );
    }

    // User is authenticated but not in employees table
    if (isAuthenticated && !hasAccess) {
        return (
            <div className="login-page">
                <div className="login-card">
                    <LoginLogo />
                    <div className="login-error-box">
                        <div className="flex justify-center mb-4">
                            <ShieldX size={48} className="text-[var(--error)]" />
                        </div>
                        <h2>Acesso Negado</h2>
                        <p>
                            Seu email não está autorizado a acessar o sistema. Entre em contato com
                            a administração.
                        </p>
                    </div>
                    <button className="login-btn secondary" onClick={signOut}>
                        <LogoutIcon />
                        Sair e tentar outra conta
                    </button>
                </div>
            </div>
        );
    }

    // Show authenticated user info (shouldn't normally show, redirect happens in App)
    if (isAuthenticated && hasAccess && employee) {
        return (
            <div className="login-page">
                <div className="login-card">
                    <LoginLogo />
                    <div className="login-welcome">
                        <p>
                            Bem-vindo(a), <strong>{employee.name}</strong>!
                        </p>
                        <p className="role-badge">{getRoleLabel(employee.role)}</p>
                    </div>
                    <div className="login-loading">
                        <div className="spinner" />
                        <span>Redirecionando...</span>
                    </div>
                </div>
            </div>
        );
    }

    // Show login form
    return (
        <div className="login-page">
            <div className="login-card">
                <LoginLogo />

                <div className="login-welcome-text">
                    <h2>Bem-vindo!</h2>
                    <p>Faça login para acessar o sistema</p>
                </div>

                {error && (
                    <div className="login-error">
                        <ErrorIcon />
                        {error}
                    </div>
                )}

                <button
                    className="login-btn google"
                    onClick={handleGoogleSignIn}
                    disabled={signingIn}
                >
                    {signingIn ? (
                        <>
                            <div className="spinner small" />
                            Conectando...
                        </>
                    ) : (
                        <>
                            <GoogleIcon />
                            Entrar com Google
                        </>
                    )}
                </button>

                <div className="login-footer">
                    <p>Sistema exclusivo para funcionários do salão</p>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// Sub-components
// ============================================================================

const LoginLogo = () => (
    <div className="login-logo">
        <div className="logo-icon">C</div>
        <h1>Conquest</h1>
        <p>Cabeleireiro</p>
    </div>
);

const LogoutIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);

const ErrorIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

const GoogleIcon = () => (
    <svg viewBox="0 0 24 24">
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
);
